import { useEffect, useRef } from 'react'

/**
 * Drives the `--parallax` custom property on a container as it crosses the
 * viewport. Layers inside it (`.depth-layer`) multiply that offset by their own
 * `--depth`, so one scroll position produces a whole stack of speeds.
 *
 * The offset is measured relative to the container rather than the document, so
 * it stays bounded (roughly ±`range`px) no matter how far down the page you are.
 *
 * @param {number} range  peak travel in px for a layer at --depth: 1
 */
export default function useParallax(range = 48) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let visible = false
    let frame = 0

    const update = () => {
      frame = 0
      const rect = el.getBoundingClientRect()
      const viewport = window.innerHeight || 1
      // -1 when the container sits below the fold, +1 once it has passed above.
      const progress = (viewport / 2 - (rect.top + rect.height / 2)) / viewport
      const clamped = Math.max(-1.5, Math.min(1.5, progress))
      el.style.setProperty('--parallax', `${(clamped * range).toFixed(2)}px`)
    }

    const onScroll = () => {
      if (!visible || frame) return
      frame = requestAnimationFrame(update)
    }

    // Only listen while the container is actually on screen.
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        if (visible) update()
      },
      { rootMargin: '10% 0px' }
    )
    io.observe(el)

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    update()

    return () => {
      io.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [range])

  return ref
}
